import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const BRAND = {
  gold: "#D4AF37",
  goldRgb: [212, 175, 55],
  ink: "#0a0a0a",
  inkRgb: [10, 10, 10],
  muted: [120, 120, 120],
  line: [232, 224, 196],
};

// Company info container, read from CompanyContext at call time.
// Default fallback used until first /api/company-settings load completes.
let _companyResolver = () => ({
  company_name: "HNC Event Catering Co.",
  logo_data_url: null,
  address: "Kuala Lumpur, Malaysia",
  phone: "+60 12-345 6001",
  email: "hello@hnccatering.com",
  website: "www.hnccatering.com",
  payment_instructions: "HNC Event Catering Sdn Bhd · Maybank 5141 1234 5678",
  tax_rate: 6.0,
});

export function setCompanyResolver(fn) {
  _companyResolver = fn;
}

function getCompany() {
  try { return _companyResolver() || {}; } catch { return {}; }
}

// ---------- CJK (Chinese) font support ----------
// jsPDF's built-in fonts can't render Chinese. We lazily fetch Noto Sans SC
// (regular + bold) once, register them, and remap every setFont call so all
// existing helvetica/times usages render through the CJK-capable font.
let _cjk; // undefined = not yet loaded, null = failed (fallback to helvetica)

async function ensureCjkFont() {
  if (_cjk !== undefined) return _cjk;
  try {
    const toB64 = async (r) => {
      const b = new Uint8Array(await r.arrayBuffer());
      let s = "";
      const CH = 0x8000;
      for (let i = 0; i < b.length; i += CH) s += String.fromCharCode.apply(null, b.subarray(i, i + CH));
      return btoa(s);
    };
    const [reg, bold] = await Promise.all([
      fetch("/fonts/NotoSansSC-Regular.ttf"),
      fetch("/fonts/NotoSansSC-Bold.ttf"),
    ]);
    if (!reg.ok || !bold.ok) throw new Error("font fetch failed");
    _cjk = { regular: await toB64(reg), bold: await toB64(bold) };
  } catch {
    _cjk = null;
  }
  return _cjk;
}

function applyCjk(doc) {
  if (!_cjk) return;
  doc.addFileToVFS("NotoSansSC-Regular.ttf", _cjk.regular);
  doc.addFileToVFS("NotoSansSC-Bold.ttf", _cjk.bold);
  doc.addFont("NotoSansSC-Regular.ttf", "NotoSansSC", "normal");
  doc.addFont("NotoSansSC-Regular.ttf", "NotoSansSC", "italic");
  doc.addFont("NotoSansSC-Bold.ttf", "NotoSansSC", "bold");
  doc.addFont("NotoSansSC-Bold.ttf", "NotoSansSC", "bolditalic");
  const orig = doc.setFont.bind(doc);
  doc.setFont = (name, style) => orig("NotoSansSC", style || "normal");
  doc.setFont("NotoSansSC", "normal");
}

async function newDoc(opts) {
  await ensureCjkFont();
  const doc = new jsPDF(opts);
  applyCjk(doc);
  return doc;
}

function header(doc, title) {
  const company = getCompany();
  // Gold band
  doc.setFillColor(...BRAND.goldRgb);
  doc.rect(0, 0, 210, 4, "F");

  // Logo (if any) — render at top-left
  if (company.logo_data_url) {
    try {
      const fmt = company.logo_data_url.startsWith("data:image/png") ? "PNG" : "JPEG";
      doc.addImage(company.logo_data_url, fmt, 15, 12, 18, 18, undefined, "FAST");
      // Brand text shifted right of logo
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...BRAND.inkRgb);
      doc.text(company.company_name || "HNC Event Catering Co.", 36, 22);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...BRAND.muted);
      doc.text((company.address || ""), 36, 27);
      doc.text(`${company.phone || ""} · ${company.email || ""}`, 36, 31);
    } catch {
      _renderTextBrand(doc, company);
    }
  } else {
    _renderTextBrand(doc, company);
  }

  // Title (right-aligned)
  doc.setFontSize(28);
  doc.setFont("times", "italic");
  doc.setTextColor(...BRAND.inkRgb);
  doc.text(title, 195, 25, { align: "right" });

  // Divider line
  doc.setDrawColor(...BRAND.line);
  doc.setLineWidth(0.3);
  doc.line(15, 40, 195, 40);
}

function _renderTextBrand(doc, company) {
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...BRAND.inkRgb);
  const nm = company.company_name || "HNC Event Catering Co.";
  // Take first word as big brand mark
  const big = nm.split(" ")[0];
  doc.text(big, 15, 22);
  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...BRAND.muted);
  doc.text(nm, 15, 27);
  doc.text(`${company.phone || ""} · ${company.email || ""}`, 15, 31);
}

function footer(doc) {
  const company = getCompany();
  const h = doc.internal.pageSize.height;
  doc.setDrawColor(...BRAND.line);
  doc.line(15, h - 20, 195, h - 20);
  doc.setFontSize(8);
  doc.setTextColor(...BRAND.muted);
  const line = [company.company_name, company.website, company.phone].filter(Boolean).join("   ·   ");
  doc.text(line, 105, h - 14, { align: "center" });
  doc.text(`Generated ${new Date().toLocaleString()}`, 105, h - 10, { align: "center" });
}

function fmtRM(n) {
  return `RM ${Number(n || 0).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-MY", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return iso || "—"; }
}

function commonBody(doc, order, opts) {
  let y = 50;
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...BRAND.muted);
  doc.text("BILL TO", 15, y);
  doc.text("EVENT", 110, y);

  doc.setTextColor(...BRAND.inkRgb);
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  y += 6;
  doc.text(order.customer_name || "", 15, y);
  doc.text(order.package_name || "", 110, y);
  y += 5;
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.muted);
  doc.text(order.customer_phone || "", 15, y);
  doc.text(`${(order.event_type || "").toUpperCase()} · ${order.pax || 0} PAX`, 110, y);
  y += 5;
  if (order.customer_email) doc.text(order.customer_email, 15, y);
  doc.text(fmtDate(order.event_date), 110, y);
  y += 5;
  doc.text(order.event_location || "", 110, y, { maxWidth: 85 });

  // Document meta box
  doc.setFillColor(250, 246, 232);
  doc.rect(140, 50, 55, 22, "F");
  doc.setTextColor(...BRAND.inkRgb);
  doc.setFontSize(7);
  doc.setFont("helvetica", "bold");
  doc.text(opts.metaLabel, 145, 56);
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.text(opts.metaValue, 145, 62);
  doc.setFontSize(7);
  doc.setTextColor(...BRAND.muted);
  doc.text(`ISSUED ${fmtDate(new Date().toISOString())}`, 145, 68);

  const items = order.items || [];
  const hasPkgTags = items.some((it) => it.package_name);
  const rows = [];
  items.forEach((it, i) => {
    // Package group header row (e.g. "Wedding Buffet A") whenever the group changes
    if (hasPkgTags) {
      const prevPkg = i > 0 ? (items[i - 1].package_name || "") : null;
      const curPkg = it.package_name || "";
      if (curPkg !== prevPkg) {
        rows.push([{
          content: curPkg || "Custom items",
          colSpan: 4,
          styles: { fontStyle: "bold", fontSize: 9, textColor: BRAND.inkRgb, fillColor: [245, 240, 225], cellPadding: 2.5 },
        }]);
      }
    }
    const uom = it.uom || "pax";
    const qty = it.quantity ? `${it.quantity} ${uom}` : `${it.pax} pax`;
    const linePrice = opts.computeLineItem(it, order);
    rows.push([
      it.name + (it.notes ? ` — ${it.notes}` : ""),
      (it.category || "").charAt(0).toUpperCase() + (it.category || "").slice(1),
      qty,
      fmtRM(linePrice),
    ]);
  });

  autoTable(doc, {
    startY: 78,
    head: [["Item", "Category", "Quantity", "Amount"]],
    body: rows,
    theme: "plain",
    headStyles: {
      fillColor: BRAND.goldRgb, textColor: BRAND.inkRgb,
      fontStyle: "bold", fontSize: 9, halign: "left", cellPadding: 3,
    },
    bodyStyles: { fontSize: 10, textColor: BRAND.inkRgb, cellPadding: 3 },
    columnStyles: {
      0: { cellWidth: 80 },
      1: { cellWidth: 35 },
      2: { cellWidth: 30 },
      3: { cellWidth: 35, halign: "right" },
    },
    didDrawCell: (data) => {
      if (data.section === "body") {
        doc.setDrawColor(...BRAND.line);
        doc.setLineWidth(0.1);
        doc.line(data.cell.x, data.cell.y + data.cell.height, data.cell.x + data.cell.width, data.cell.y + data.cell.height);
      }
    },
  });

  const finalY = doc.lastAutoTable.finalY + 6;
  const totals = opts.totals(order);
  let ty = finalY;
  doc.setFontSize(9);
  totals.forEach((row) => {
    doc.setTextColor(...(row.bold ? BRAND.inkRgb : BRAND.muted));
    doc.setFont("helvetica", row.bold ? "bold" : "normal");
    doc.setFontSize(row.bold ? 12 : 9);
    doc.text(row.label, 130, ty);
    doc.text(row.value, 195, ty, { align: "right" });
    if (row.bold) {
      doc.setDrawColor(...BRAND.goldRgb);
      doc.setLineWidth(0.4);
      doc.line(130, ty - 4, 195, ty - 4);
    }
    ty += row.bold ? 8 : 6;
  });

  if (opts.notes) {
    ty += 4;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...BRAND.muted);
    doc.text("NOTES", 15, ty);
    ty += 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...BRAND.inkRgb);
    doc.text(opts.notes, 15, ty, { maxWidth: 180 });
  }
}

// -------- Utensil checklist (per order) --------
export async function downloadUtensilChecklist(order, items) {
  const doc = await newDoc();
  header(doc, "UTENSIL CHECKLIST");

  // Order block
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.muted);
  doc.text("EVENT / ORDER", 15, 52);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...BRAND.inkRgb);
  doc.text(order.job_title || order.customer_name || "", 15, 58);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...BRAND.muted);
  doc.text(`Customer: ${order.customer_name || ""}`, 15, 64);
  doc.text(`Date: ${fmtDate(order.event_date)}`, 15, 69);
  doc.text(`Venue: ${order.event_location || ""}`, 15, 74);
  doc.text(`Generated: ${new Date().toLocaleString("en-MY")}`, 130, 64);

  const rows = (items || []).map((it) => [
    it.checked ? "[ / ]" : "[   ]",
    "", // photo cell — drawn in didDrawCell
    it.label,
    `${it.qty} ${it.uom || ""}`.trim(),
    it.checked && it.checked_by_name
      ? `${it.checked_by_name}`
      : (it.created_by_name || ""),
  ]);
  autoTable(doc, {
    startY: 82,
    head: [["✓", "Photo", "Utensil", "Qty", "By"]],
    body: rows.length ? rows : [["", "", "No utensils assigned to this order", "", ""]],
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 3, lineColor: BRAND.line, textColor: BRAND.inkRgb, minCellHeight: 16 },
    headStyles: { fillColor: BRAND.inkRgb, textColor: [255, 255, 255], fontSize: 8 },
    columnStyles: {
      0: { cellWidth: 12, halign: "center" },
      1: { cellWidth: 20, halign: "center" },
      3: { cellWidth: 22 },
      4: { cellWidth: 40 },
    },
    didDrawCell: (hookData) => {
      // Draw the utensil photo inside column index 1 of each body row
      if (hookData.section === "body" && hookData.column.index === 1) {
        const it = (items || [])[hookData.row.index];
        const img = it?.image_data_url;
        if (img) {
          try {
            const fmt = img.startsWith("data:image/png") ? "PNG" : "JPEG";
            const size = 13;
            const x = hookData.cell.x + (hookData.cell.width - size) / 2;
            const y = hookData.cell.y + (hookData.cell.height - size) / 2;
            doc.addImage(img, fmt, x, y, size, size, undefined, "FAST");
          } catch { /* skip unreadable image */ }
        }
      }
    },
  });

  const y = (doc.lastAutoTable?.finalY || 100) + 14;
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.muted);
  doc.text("Prepared by: ______________________", 15, y);
  doc.text("Verified by: ______________________", 115, y);

  footer(doc);
  doc.save(`utensil-checklist-${(order.customer_name || "order").replace(/\s+/g, "-")}.pdf`);
}


export async function downloadQuotation(order) {
  const company = getCompany();
  const doc = await newDoc({ unit: "mm", format: "a4" });
  header(doc, "Quotation");

  const tax = company.tax_rate || 6;
  commonBody(doc, order, {
    metaLabel: "QUOTATION #",
    metaValue: `Q-${(order.tracking_code || "").slice(0, 8)}`,
    computeLineItem: (it, ord) => {
      const total = ord.total_amount || 0;
      const items = ord.items || [];
      const totalWeight = items.reduce((s, i) => s + (i.pax > 0 ? i.pax : (i.quantity || 0)), 0) || 1;
      const w = it.pax > 0 ? it.pax : (it.quantity || 0);
      return (w / totalWeight) * total;
    },
    totals: (ord) => {
      const sub = ord.total_amount || 0;
      const dfee = ord.delivery_fee || 0;
      const sst = sub * (tax / 100);
      const rows = [{ label: "Subtotal", value: fmtRM(sub) }];
      if (dfee > 0) rows.push({ label: "Delivery fee", value: fmtRM(dfee) });
      rows.push({ label: `Service tax (${tax}%)`, value: fmtRM(sst) });
      rows.push({ label: "Total quotation", value: fmtRM(sub + dfee + sst), bold: true });
      return rows;
    },
    notes: "Valid for 30 days from issue date. Final pricing confirmed upon booking with 30% deposit. Menu adjustments possible up to 14 days before event.",
  });

  footer(doc);
  doc.save(`Quotation-${order.customer_name?.replace(/\s+/g, "")}.pdf`);
}

export async function downloadInvoice(order) {
  const company = getCompany();
  const doc = await newDoc({ unit: "mm", format: "a4" });
  const num2 = (n) => Number(n || 0).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // ---- Header: logo + company info (left) · INVOICE meta (right) ----
  let cx = 15;
  if (company.logo_data_url) {
    try {
      const fmt = company.logo_data_url.startsWith("data:image/png") ? "PNG" : "JPEG";
      doc.addImage(company.logo_data_url, fmt, 15, 12, 24, 24, undefined, "FAST");
      cx = 44;
    } catch { /* logo unreadable — text-only header */ }
  }
  doc.setFontSize(13);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...BRAND.inkRgb);
  doc.text(company.company_name || "HNC Event Catering Co.", cx, 17);
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...BRAND.muted);
  let cyy = 22;
  if (company.phone) { doc.text(company.phone, cx, cyy); cyy += 4; }
  if (company.email) { doc.text(company.email, cx, cyy); cyy += 4; }
  if (company.website) { doc.text(company.website, cx, cyy); cyy += 4; }
  if (company.address) { doc.text(doc.splitTextToSize(company.address, 92), cx, cyy); }

  doc.setFontSize(24);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...BRAND.inkRgb);
  doc.text("INVOICE", 195, 20, { align: "right" });
  doc.setFontSize(12);
  doc.text(`${order.invoice_number || `HNC-${order.tracking_code || ""}`}`, 195, 28, { align: "right" });
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...BRAND.muted);
  const evd = order.invoice_date
    ? String(order.invoice_date).slice(0, 10)
    : (order.event_date ? `${String(order.event_date).slice(0, 10)} ${String(order.event_date).slice(11, 16)}`.trim() : "");
  doc.text(evd, 195, 34, { align: "right" });

  doc.setDrawColor(...BRAND.line);
  doc.setLineWidth(0.3);
  doc.line(15, 42, 195, 42);

  // ---- BILL TO / EVENT ----
  let y = 50;
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...BRAND.muted);
  doc.text("BILL TO", 15, y);
  doc.text("EVENT", 110, y);
  y += 6;
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...BRAND.inkRgb);
  doc.text(order.customer_name || "", 15, y);
  doc.text(order.job_title || order.package_name || "", 110, y);
  y += 5;
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.muted);
  doc.text(order.customer_phone || "", 15, y);
  doc.text(`${order.pax || 0} pax${order.job_title ? ` · ${order.package_name || ""}` : ""}`, 110, y);
  y += 5;
  if (order.customer_email) doc.text(order.customer_email, 15, y);
  doc.text(doc.splitTextToSize(order.event_location || "", 85), 110, y);

  // ---- Items table: Item | Qty | Unit (RM) | Total (RM) ----
  const items = order.items || [];
  const priced = (order.packages_pricing || []);
  const rows = [];
  let prevPkg = null;
  items.forEach((it) => {
    const curPkg = it.package_name || "";
    if (curPkg !== prevPkg) {
      prevPkg = curPkg;
      if (curPkg) {
        const pp = priced.find((p) => p.package_name === curPkg);
        let qtyCell = "", unitCell = "", totCell = "";
        if (pp && pp.unit_price > 0) {
          if (pp.pricing_mode === "set") {
            qtyCell = `${pp.quantity || 1} set`;
            unitCell = num2(pp.unit_price);
            totCell = num2(pp.unit_price * (pp.quantity || 1));
          } else {
            const px = pp.pax || order.pax || 0;
            qtyCell = `${px} pax`;
            unitCell = num2(pp.unit_price);
            totCell = num2(pp.unit_price * px);
          }
        }
        rows.push([
          { content: curPkg, styles: { fontStyle: "bold", textColor: BRAND.inkRgb } },
          { content: qtyCell, styles: { fontStyle: "bold", halign: "center" } },
          { content: unitCell, styles: { fontStyle: "bold", halign: "right" } },
          { content: totCell, styles: { fontStyle: "bold", halign: "right" } },
        ]);
      }
    }
    if (curPkg) {
      // package sub-item — bulleted, no individual pricing
      rows.push([`  •  ${it.name}${it.notes ? ` — ${it.notes}` : ""}`, "", "", ""]);
    } else {
      const qty = it.quantity ? `${it.quantity} ${it.uom || "pax"}` : `${it.pax} pax`;
      const up = Number(it.unit_price || 0);
      const qn = it.quantity || it.pax || 0;
      rows.push([
        it.name + (it.notes ? ` — ${it.notes}` : ""),
        { content: qty, styles: { halign: "center" } },
        { content: num2(up), styles: { halign: "right" } },
        { content: num2(up * qn), styles: { halign: "right" } },
      ]);
    }
  });

  autoTable(doc, {
    startY: y + 8,
    head: [["Item", { content: "Qty", styles: { halign: "center" } }, { content: "Unit (RM)", styles: { halign: "right" } }, { content: "Total (RM)", styles: { halign: "right" } }]],
    body: rows.length ? rows : [["—", "", "", ""]],
    theme: "plain",
    headStyles: { fillColor: [245, 245, 245], textColor: BRAND.inkRgb, fontStyle: "bold", fontSize: 9, cellPadding: 3 },
    bodyStyles: { fontSize: 9.5, textColor: BRAND.inkRgb, cellPadding: 2.5 },
    columnStyles: { 0: { cellWidth: 95 }, 1: { cellWidth: 27 }, 2: { cellWidth: 29 }, 3: { cellWidth: 29 } },
    didDrawCell: (data) => {
      if (data.section === "body" && data.column.index === 0) {
        doc.setDrawColor(235, 235, 235);
        doc.setLineWidth(0.1);
        doc.line(15, data.cell.y + data.cell.height, 195, data.cell.y + data.cell.height);
      }
    },
  });

  // ---- Totals ----
  const sub = Number(order.total_amount || 0);
  const dfee = Number(order.delivery_fee || 0);
  const disc = Number(order.discount || 0);
  const invTotal = sub + dfee - disc;
  const paid = Number(order.deposit_paid || 0);
  const bal = invTotal - paid;
  const totals = [
    ["Subtotal", num2(sub), false],
    ["Delivery", num2(dfee), false],
    ["Discount", disc > 0 ? `- ${num2(disc)}` : num2(0), false],
    ["Invoice total", num2(invTotal), true],
    ["Deposit paid", num2(paid), false],
    ["Balance due", num2(Math.max(0, bal)), true],
  ];
  let ty = (doc.lastAutoTable?.finalY || y + 20) + 8;
  if (ty > 240) { doc.addPage(); ty = 24; }
  totals.forEach(([label, value, bold]) => {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(bold ? 11 : 9);
    doc.setTextColor(...(bold ? BRAND.inkRgb : BRAND.muted));
    if (bold) {
      doc.setDrawColor(...BRAND.goldRgb);
      doc.setLineWidth(0.4);
      doc.line(128, ty - 4.5, 195, ty - 4.5);
    }
    doc.text(`${label}`, 128, ty);
    doc.text(`RM ${value}`, 195, ty, { align: "right" });
    ty += bold ? 8 : 6;
  });

  // ---- Payment details + DuitNow QR ----
  let py = ty + 4;
  if (py > 235) { doc.addPage(); py = 24; }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...BRAND.muted);
  doc.text("PAYMENT DETAILS", 15, py);
  py += 5;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.inkRgb);
  const payLines = doc.splitTextToSize(
    `${company.payment_instructions || "Bank details on request"}\nPlease include the tracking code (${order.tracking_code || ""}) as payment reference.`,
    company.duitnow_qr_data_url ? 128 : 175
  );
  doc.text(payLines, 15, py);
  let afterPay = py + payLines.length * 4.2;
  if (company.duitnow_qr_data_url) {
    try {
      const qfmt = company.duitnow_qr_data_url.startsWith("data:image/png") ? "PNG" : "JPEG";
      doc.addImage(company.duitnow_qr_data_url, qfmt, 155, py - 4, 34, 34, undefined, "FAST");
      doc.setFontSize(7.5);
      doc.setTextColor(...BRAND.muted);
      doc.text("Scan DuitNow QR", 172, py + 33, { align: "center" });
      afterPay = Math.max(afterPay, py + 36);
    } catch { /* unreadable QR image — skip */ }
  }

  // ---- Notes ----
  if (order.notes) {
    let ny = afterPay + 6;
    if (ny > 255) { doc.addPage(); ny = 24; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...BRAND.muted);
    doc.text("NOTES", 15, ny);
    ny += 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...BRAND.inkRgb);
    doc.text(doc.splitTextToSize(order.notes, 175), 15, ny);
  }

  footer(doc);
  // Page numbers
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...BRAND.muted);
    doc.text(`Page ${i}`, 195, doc.internal.pageSize.height - 6, { align: "right" });
  }
  doc.save(`Invoice-${((order.invoice_number || `HNC-${order.tracking_code || ""}`).replace(/[^\w\u4e00-\u9fff-]/g, "")) || "order"}.pdf`);
}

// Full enquiry summary PDF — all details incl. selected packages/menu & dishes
export async function downloadEnquiry(enq) {
  const doc = await newDoc({ unit: "mm", format: "a4" });
  const isCorp = enq.type === "corporate";
  header(doc, isCorp ? "Quotation Request" : "Enquiry");

  doc.setFontSize(9);
  doc.setTextColor(...BRAND.muted);
  doc.text("REFERENCE", 15, 50);
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...BRAND.inkRgb);
  doc.text(enq.ref_code || "", 15, 56);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...BRAND.muted);
  doc.text(`Status: ${(enq.status || "new").toUpperCase()}`, 130, 50);
  doc.text(`Submitted: ${fmtDate(enq.created_at)}`, 130, 55);

  const details = [
    ["Type", isCorp ? "Corporate Quotation" : "Customer Order"],
    isCorp ? ["Company", enq.company_name] : ["Name", enq.name],
    ["Contact Person", enq.contact_person || enq.name],
    ["Phone", enq.phone],
    ["Email", enq.email],
    ["Invoice Name", enq.invoice_name],
    ["Event Name", enq.event_name],
    ["Event Category", enq.event_category],
    ["Event Date", fmtDate(enq.event_date)],
    ["Event Time", [enq.event_time, enq.event_end_time].filter(Boolean).join(" – ")],
    ["Pax", enq.pax ? String(enq.pax) : ""],
    ["Theme", enq.theme],
    ["Theme Colour", enq.theme_colour],
    ["Budget / pax", enq.budget_per_pax ? fmtRM(enq.budget_per_pax) : ""],
    ["Billing Address", enq.billing_address],
    ["Delivery Address", enq.delivery_address || enq.address],
    ["Services", (enq.services || []).join(", ")],
    ["Menu Notes", enq.menu_notes],
  ].filter(([, v]) => v);

  autoTable(doc, {
    startY: 62,
    body: details,
    theme: "plain",
    styles: { fontSize: 9, cellPadding: 1.8, textColor: BRAND.inkRgb },
    columnStyles: { 0: { cellWidth: 42, fontStyle: "bold", textColor: BRAND.muted } },
  });

  let y = (doc.lastAutoTable?.finalY || 70) + 6;

  const ensureSpace = (needed) => {
    if (y + needed > doc.internal.pageSize.height - 28) {
      doc.addPage();
      y = 20;
    }
  };

  // Selected packages & menu
  (enq.package_selections || []).forEach((ps) => {
    ensureSpace(20);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...BRAND.goldRgb);
    const setLabel = ps.pricing_mode === "set" ? `  ×${ps.quantity || 1} set(s)` : "";
    const priceLabel = ps.unit_price ? `  (${fmtRM(ps.unit_price)} / ${ps.pricing_mode === "set" ? "set" : "pax"})` : "";
    doc.text(`Package: ${ps.package_name}${setLabel}${priceLabel}`, 15, y);
    y += 3;
    const rows = (ps.choices || []).map((c) => [c.slot || "", `${c.item_name}${c.variant ? ` — ${c.variant}` : ""}`]);
    autoTable(doc, {
      startY: y,
      head: [["Course / Slot", "Selected Dish"]],
      body: rows.length ? rows : [["—", "No selections recorded"]],
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 2.5, lineColor: BRAND.line, textColor: BRAND.inkRgb },
      headStyles: { fillColor: BRAND.inkRgb, textColor: [255, 255, 255], fontSize: 8 },
      columnStyles: { 0: { cellWidth: 50 } },
    });
    y = (doc.lastAutoTable?.finalY || y) + 8;
  });

  // À la carte dishes
  if ((enq.items || []).length > 0) {
    ensureSpace(20);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...BRAND.goldRgb);
    doc.text("À La Carte Dishes", 15, y);
    y += 3;
    autoTable(doc, {
      startY: y,
      head: [["Dish", "Qty", "Notes"]],
      body: enq.items.map((i) => [i.name, `${i.quantity} ${i.uom || ""}`.trim(), i.notes || ""]),
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 2.5, lineColor: BRAND.line, textColor: BRAND.inkRgb },
      headStyles: { fillColor: BRAND.inkRgb, textColor: [255, 255, 255], fontSize: 8 },
      columnStyles: { 1: { cellWidth: 28 } },
    });
    y = (doc.lastAutoTable?.finalY || y) + 8;
  }

  // Remarks / requirements
  const remarks = enq.remarks || enq.requirements;
  if (remarks) {
    ensureSpace(24);
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...BRAND.muted);
    doc.text("REMARKS", 15, y);
    y += 5;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...BRAND.inkRgb);
    const lines = doc.splitTextToSize(remarks, 175);
    doc.text(lines, 15, y);
  }

  footer(doc);
  doc.save(`Enquiry-${enq.ref_code || "details"}.pdf`);
}

/**
 * Generic report PDF.
 * @param {Object} opts
 * @param {string} opts.title - Display title (e.g. "Sales Report")
 * @param {string} opts.filename - PDF filename
 * @param {Array<{label:string,value:string}>} [opts.kpis] - Top KPI tiles
 * @param {Array<{title:string, columns:string[], rows:Array<Array<string>>, columnStyles?:object}>} opts.sections - Tabular sections
 * @param {string} [opts.subtitle] - Subtitle under title
 */
export async function downloadReport(opts) {
  const doc = await newDoc({ unit: "mm", format: "a4" });
  header(doc, opts.title);

  let y = 50;
  if (opts.subtitle) {
    doc.setFontSize(10);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(...BRAND.muted);
    doc.text(opts.subtitle, 15, y);
    y += 6;
  }

  // KPIs
  if (opts.kpis && opts.kpis.length > 0) {
    const cols = Math.min(opts.kpis.length, 4);
    const w = (180) / cols;
    opts.kpis.slice(0, 4).forEach((k, i) => {
      const x = 15 + i * w;
      doc.setFillColor(250, 246, 232);
      doc.rect(x, y, w - 3, 18, "F");
      doc.setFontSize(7);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...BRAND.muted);
      doc.text(k.label.toUpperCase(), x + 3, y + 6);
      doc.setFontSize(13);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...BRAND.inkRgb);
      doc.text(String(k.value), x + 3, y + 14);
    });
    y += 24;
  }

  for (const section of opts.sections || []) {
    if (y > 250) { doc.addPage(); y = 30; }
    if (section.title) {
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...BRAND.inkRgb);
      doc.text(section.title, 15, y);
      y += 4;
    }
    autoTable(doc, {
      startY: y,
      head: [section.columns],
      body: section.rows,
      theme: "plain",
      headStyles: { fillColor: BRAND.goldRgb, textColor: BRAND.inkRgb, fontStyle: "bold", fontSize: 9, halign: "left", cellPadding: 3 },
      bodyStyles: { fontSize: 9, textColor: BRAND.inkRgb, cellPadding: 2.5 },
      alternateRowStyles: { fillColor: [250, 248, 240] },
      columnStyles: section.columnStyles || {},
      didDrawPage: (data) => {
        // ensure header reappears on page breaks
        if (data.pageCount > 1 && data.cursor.y < 40) {
          header(doc, opts.title);
        }
      },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  footer(doc);
  doc.save(opts.filename || "report.pdf");
}

/**
 * Branded Purchase Order PDF — supplier-facing document.
 * @param {object} po - purchase order from /api/purchase-orders
 * @param {object} supplier - the matching supplier {name, contact_person, phone, email, address, payment_terms}
 */
export async function downloadPurchaseOrder(po, supplier = {}) {
  const company = getCompany();
  const doc = await newDoc({ unit: "mm", format: "a4" });
  header(doc, "Purchase Order");

  // Meta block: PO #, dates, status, supplier
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...BRAND.muted);
  doc.text("PO NUMBER", 130, 50);
  doc.text("ISSUE DATE", 130, 60);
  if (po.expected_date) doc.text("EXPECTED", 165, 60);
  doc.text("STATUS", 130, 70);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(...BRAND.inkRgb);
  doc.text(po.po_number || "—", 130, 55);
  doc.setFontSize(9);
  doc.text(po.created_at ? new Date(po.created_at).toLocaleDateString("en-MY", { day: "numeric", month: "short", year: "numeric" }) : "—", 130, 65);
  if (po.expected_date) doc.text(new Date(po.expected_date).toLocaleDateString("en-MY", { day: "numeric", month: "short", year: "numeric" }), 165, 65);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text((po.status || "draft").toUpperCase(), 130, 75);

  // Supplier (bill-to) block
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...BRAND.muted);
  doc.text("SUPPLIER", 15, 50);
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...BRAND.inkRgb);
  doc.text(supplier.name || "—", 15, 56);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  let sy = 61;
  if (supplier.contact_person) { doc.text(`Attn: ${supplier.contact_person}`, 15, sy); sy += 5; }
  if (supplier.phone) { doc.text(supplier.phone, 15, sy); sy += 5; }
  if (supplier.email) { doc.text(supplier.email, 15, sy); sy += 5; }
  if (supplier.address) { doc.text(supplier.address, 15, sy, { maxWidth: 100 }); sy += 5; }

  // Line items
  const items = po.items || [];
  const rows = items.map((it) => {
    const qty = `${it.qty ?? 0}${it.unit ? " " + it.unit : ""}`;
    const unitPrice = Number(it.unit_price || 0);
    const line = Number(it.qty || 0) * unitPrice;
    return [it.name || "—", qty, fmtRM(unitPrice), fmtRM(line)];
  });

  autoTable(doc, {
    startY: 88,
    head: [["Item", "Quantity", "Unit Price", "Line Total"]],
    body: rows.length ? rows : [["—", "—", "—", "—"]],
    theme: "plain",
    headStyles: { fillColor: BRAND.goldRgb, textColor: BRAND.inkRgb, fontStyle: "bold", fontSize: 9, halign: "left", cellPadding: 3 },
    bodyStyles: { fontSize: 10, textColor: BRAND.inkRgb, cellPadding: 3 },
    columnStyles: {
      0: { cellWidth: 90 },
      1: { cellWidth: 30 },
      2: { cellWidth: 30, halign: "right" },
      3: { cellWidth: 30, halign: "right" },
    },
    didDrawCell: (data) => {
      if (data.section === "body") {
        doc.setDrawColor(...BRAND.line);
        doc.setLineWidth(0.1);
        doc.line(data.cell.x, data.cell.y + data.cell.height, data.cell.x + data.cell.width, data.cell.y + data.cell.height);
      }
    },
  });

  // Totals block
  const subTotal = Number(po.total || items.reduce((s, i) => s + (Number(i.qty || 0) * Number(i.unit_price || 0)), 0));
  const finalY = doc.lastAutoTable.finalY + 6;
  let ty = finalY;
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...BRAND.muted);
  doc.text("Subtotal", 130, ty);
  doc.text(fmtRM(subTotal), 195, ty, { align: "right" });
  ty += 8;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...BRAND.inkRgb);
  doc.setDrawColor(...BRAND.goldRgb);
  doc.setLineWidth(0.4);
  doc.line(130, ty - 4, 195, ty - 4);
  doc.text("Total", 130, ty);
  doc.text(fmtRM(subTotal), 195, ty, { align: "right" });
  ty += 10;

  // Payment terms + notes
  if (supplier.payment_terms) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...BRAND.muted);
    doc.text("PAYMENT TERMS", 15, ty);
    ty += 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...BRAND.inkRgb);
    doc.text(supplier.payment_terms, 15, ty);
    ty += 8;
  }
  if (po.notes) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...BRAND.muted);
    doc.text("NOTES", 15, ty);
    ty += 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...BRAND.inkRgb);
    doc.text(po.notes, 15, ty, { maxWidth: 180 });
  }

  footer(doc);
  const slug = (supplier.name || "supplier").replace(/\s+/g, "-");
  doc.save(`${po.po_number || "PO"}-${slug}.pdf`);
}
